/**
 * HITL (Human-in-the-Loop) Types
 *
 * Shared type definitions for HITL approval workflows in the chat UI.
 */

/** Decision types from HITLRequestConfirmationDecision */
export type HITLDecisionType = 'approve' | 'reject' | 'edit';

/** Status for HITL pending state (before decision) */
export type HITLStatus = 'pending' | HITLDecisionType;

export type HITLActionHandlers = {
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  disabled?: boolean;
};

export type BackgroundExecutionReference = {
  threadId: string;
  isRunning?: boolean;
  terminalStatus?: 'completed' | 'error' | 'aborted';
};

export type GetBackgroundExecutionByToolCallId = (
  toolCallId: string,
) => BackgroundExecutionReference | undefined;

export type HITLEditInMainContentData = {
  toolName: string;
  args: Record<string, unknown>;
  hitlMessageId: string;
  objectType?: string;
  title?: string;
  onEditComplete: (editedArgs: Record<string, unknown>) => void;
  onEditCancel?: () => void;
  onApprove?: () => void;
  onReject?: (message?: string) => void;
};

export type OnOpenEditInMainContent = (data: HITLEditInMainContentData) => void;

export type OnCloseEditInMainContent = (hitlMessageId: string) => void;

export type HttpViewerTabData = {
  request: {
    method: string;
    url: string;
    headers: Record<string, string | string[]>;
    body?: string;
    httpVersion?: string;
  } | null;
  response: {
    status?: number;
    statusMessage?: string;
    headers?: Record<string, string | string[]>;
    body?: string;
    httpVersion?: string;
  } | null;
  fallbackOutput?: unknown;
  toolName?: string;
  title?: string;
};

export type OnOpenHttpInMainContent = (data: HttpViewerTabData) => void;

export type ChatObjectNavigationTarget =
  | {
      type: 'endpoint';
      id: string;
      label?: string;
      endpointUrl?: string;
    }
  | {
      type: 'finding';
      id: string;
      label?: string;
      mode?: 'view' | 'edit';
    }
  | {
      type: 'signal';
      id: string;
      label?: string;
    }
  | {
      type: 'webpage';
      id: string;
      label?: string;
    }
  | {
      type: 'request';
      id: string;
      label?: string;
    }
  | {
      type: 'execution';
      id: string;
      label?: string;
    }
  | {
      type: 'agent-execution';
      id: string;
      label?: string;
    }
  | {
      type: 'skill-draft';
      id: string;
      label?: string;
      mode: 'create' | 'edit';
      skillId?: string;
    };

export type OnOpenChatObject = (target: ChatObjectNavigationTarget) => void;

export type SkillDraftReference = Extract<
  ChatObjectNavigationTarget,
  { type: 'skill-draft' }
>;

export type TerminalResult = {
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  duration?: number;
};

/**
 * Parse terminal output into structured result
 */
export function parseTerminalResult(
  output: unknown,
): TerminalResult | undefined {
  if (!output) return undefined;

  if (typeof output === 'object' && output !== null) {
    const obj = output as Record<string, unknown>;

    return {
      stdout: typeof obj.stdout === 'string' ? obj.stdout : undefined,
      stderr: typeof obj.stderr === 'string' ? obj.stderr : undefined,
      exitCode: typeof obj.exitCode === 'number' ? obj.exitCode : undefined,
      duration: typeof obj.duration === 'number' ? obj.duration : undefined,
    };
  }

  if (typeof output === 'string') {
    const exitCodeMatch = output.match(/Exit Code: (\d+)/);
    const durationMatch = output.match(/Duration: (\d+)ms/);
    const stdoutMatch = output.match(
      /=== STDOUT ===\n([\s\S]*?)(?:\n\n=== STDERR ===|$)/,
    );
    const stderrMatch = output.match(/=== STDERR ===\n([\s\S]*?)$/);

    return {
      exitCode: exitCodeMatch ? parseInt(exitCodeMatch[1], 10) : undefined,
      duration: durationMatch ? parseInt(durationMatch[1], 10) : undefined,
      stdout: stdoutMatch ? stdoutMatch[1].trim() : output,
      stderr: stderrMatch ? stderrMatch[1].trim() : undefined,
    };
  }

  return undefined;
}
